import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BsXLg } from "react-icons/bs";
import styled from "styled-components";
import { getMovieDetails } from "../../services/tmdb";
import MovieDetailsContent from "../MovieDetailsContent";
import PageLoader from "../PageLoader";
import SectionMessage from "../SectionMessage";

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(12px);
`;

const Dialog = styled(motion.div)`
  position: relative;
  width: min(1040px, 100%);
  max-height: min(92vh, 1000px);
  overflow: auto;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: ${({ theme }) => theme.colors.glassStrong};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.strong};
`;

const CloseButton = styled.button`
  position: sticky;
  top: 0;
  z-index: 2;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  background: rgba(255, 255, 255, 0.08);
`;

function MovieModal({ movieId, genresById, onClose, onSelectMovie }) {
  const [retryCount, setRetryCount] = useState(0);
  const [state, setState] = useState({ loading: false, error: "", movie: null });

  useEffect(() => {
    if (!movieId) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.classList.add("modal-open");
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [movieId, onClose]);

  useEffect(() => {
    if (!movieId) {
      return;
    }

    let active = true;

    async function loadMovie() {
      try {
        setState({ loading: true, error: "", movie: null });
        const movie = await getMovieDetails(movieId, genresById);

        if (!active) {
          return;
        }

        setState({ loading: false, error: "", movie });
      } catch (error) {
        if (!active) {
          return;
        }

        setState({ loading: false, error: "Nao foi possivel carregar os detalhes agora.", movie: null });
      }
    }

    loadMovie();

    return () => {
      active = false;
    };
  }, [genresById, movieId, retryCount]);

  return (
    <AnimatePresence>
      {movieId ? (
        <Backdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          aria-modal="true"
          role="dialog"
          aria-label="Detalhes do filme"
        >
          <Dialog
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.24, ease: "easeOut" }}
            onClick={(event) => event.stopPropagation()}
          >
            <CloseButton type="button" aria-label="Fechar modal" onClick={onClose}>
              <BsXLg />
            </CloseButton>
            {state.loading ? <PageLoader compact label="Carregando os detalhes do titulo..." /> : null}
            {!state.loading && state.error ? (
              <SectionMessage
                title="Falha ao abrir este titulo"
                description={state.error}
                actionLabel="Tentar novamente"
                onAction={() => setRetryCount((currentRetryCount) => currentRetryCount + 1)}
              />
            ) : null}
            {!state.loading && state.movie ? (
              <MovieDetailsContent movie={state.movie} mode="modal" onSelectMovie={onSelectMovie} />
            ) : null}
          </Dialog>
        </Backdrop>
      ) : null}
    </AnimatePresence>
  );
}

export default MovieModal;
